import { GetPointResponse, ImageWithType, PointCardView } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { Accordion } from '@vaadin/vaadin-accordion';
import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { ifDefined } from 'lit/directives/if-defined.js';
import { isDefined } from '../helpers/utils';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('point-list')
export class PointList extends LitElement {
    static amplitude_callsite: string = 'Point List';



    @query('#accordion')
    accordionElement?: Accordion;

    @query('#editPointModal')
    editPointModal!: Dialog;

    @query('#sharePointUrl')
    sharePointUrlModal?: Dialog;

    @state()
    imageUrls: ImageWithType[] = [];

    @state()
    searchEverywhereClicked: boolean = false;

    @state()
    openedPoint?: GetPointResponse;

    @state()
    mobilePreview: boolean = false;

    @state()
    maxLoad: number = 1000;

    @state()
    location: string = '';

    @state()
    isCreatorInfoComponent: boolean = false;

    @state()
    pointsToLoad: any[] = [];

    @state()
    listView: PointCardView = PointCardView.List;

    @state()
    get pointUrl() {
        if (isDefined(this.openedPoint)) {
            return `${window.location.href.split('?')[0]}?topic_id=${topicData.topicId}&point_id=${this.openedPoint?._id.$oid}`;
        }
        return '';
    }

    firstUpdated() {
        if (this.location === 'creator-info-component') {
            this.isCreatorInfoComponent = true;
        }

        this.listView = topicData.topic?.default_list_view === PointCardView.Table ? PointCardView.Cards : topicData.topic?.default_list_view ?? PointCardView.List;
    }

    // Override default behavior to automatially open the first panel

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    // if this.searchEverywhereisChanged and true, rerender
    updated(changedProperties: Map<string, any>) {
        if (changedProperties.has('searchEverywhereClicked')) {
            this.render();
        }
    }

    updateView(view: PointCardView) {
        this.listView = view;
    }

    async focusPoint(point: GetPointResponse) {
        if (topicData.mapComponent) {
            await topicData.mapComponent.onClickPoint(point);
        }
    }

    render() {
        this.pointsToLoad = (this.isCreatorInfoComponent && !this.searchEverywhereClicked)
            ? pointCache.markersInBounds
            : pointCache.orderedPoints;

        const loadedPoints = this.pointsToLoad.slice(0, this.maxLoad);

        return html`
            ${this.location !== 'creator-info-component' ? html`
                <div class="view_options">
                    <form-button
                        .icon=${'view_agenda'}
                        variant="outline"
                        size="sm"
                        @click="${() => this.updateView(PointCardView.List)}"
                    ></form-button>
                    <form-button
                        .icon=${'view_module'}
                        variant="outline"
                        size="sm"
                        @click="${() => this.updateView(PointCardView.Cards)}"
                    ></form-button>
                </div>
            ` : ''}
            ${this.renderCards(loadedPoints)}
            ${this.pointUrl ? html `
                <mwc-dialog id="sharePointUrl" class="point-card__share-modal" hideActions>
                    <modal-dialog size="lg" @close="${() => this.sharePointUrlModal?.close()}">
                        <map-sharing-component
                                slot="body"
                                .isPointLink="${true}"
                                socialMediaShareImage="${ifDefined(topicData.topic?.custom_social_media_image)}"
                                viewonlyLink="${ifDefined(this.pointUrl)}"
                            >
                        </map-sharing-component>
                    </modal-dialog>
                </mwc-dialog>
            ` : ''}

            <error-toast id="errorToast"></error-toast>
        `;
    }

    renderCards(points: any[]) {
        if (this.listView === PointCardView.Cards) {
            return this.renderGridCards(points);
        } else {
            return this.renderListCards(points);
        }
    }

    renderGridCards(points: any[]) {
        return html`
            <div class="grid-view-holder">
                ${points.map((place, i) => {
                    const point = (this.isCreatorInfoComponent && !this.searchEverywhereClicked) ? place.point : place;
                    return html`
                    <grid-point-card .point=${point} @on-card-click="${async () => await this.focusPoint(point)}">
                    </grid-point-card>`;
                    }
                )}
                ${points.length < this.pointsToLoad.length ? html`
                    <a class="load-more" @click="${() => { this.maxLoad += 100; }}">Load More</a>
                ` : ''}
            </div>
        `;
    }

    renderListCards(points: any[]) {
        return html`
            <div class="list-view-holder">
                ${points.map((place, i) => {
                    const point = (this.isCreatorInfoComponent && !this.searchEverywhereClicked) ? place.point : place;
                    return html`
                    <list-point-card .point=${point} @on-card-click="${async () => await this.focusPoint(point)}">
                    </list-point-card>`;
                    }
                )}
                ${points.length < this.pointsToLoad.length ? html`
                    <a class="load-more" @click="${() => { this.maxLoad += 100; }}">Load More</a>
                ` : ''}
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                width: 100%;
                height: auto;
                display: flex;
                flex-direction: column;
            }

            /* width */
            :host::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            :host::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            :host::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .view_options {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: flex-end;
                gap: 10px;
                margin-bottom: 10px;
            }

            .grid-view-holder {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 10px;
            }

            .list-view-holder {
                display: flex;
                flex-direction: column;
                width: 100%;
                gap:2px;
                margin-top: 2px;
            }

            .wide {
                width: 100%;
            }

            .summary {
                width: 100%;
                display: inline-flex;
            }

            #summaryName {
                padding-left: 10px;
                padding-right: 10px;
                min-width: 200px;
                text-overflow: ellipsis;
                overflow: hidden;
                width: 18vw;
                white-space: nowrap;
            }

            #summaryName.mobile {
                width: 60vw;
            }

            .load-more {
                text-decoration: underline;
                cursor: pointer;
            }
        `;
    }
}
